// قطع واجهة مشتركة بين الشاشات — لوحة الأرقام وأشرطة البُعدين والصفوف.

import { esc, fmt, money, shortDate } from './util.js';
import * as calc from './calc.js';
import { CURRENCIES, destName, typeName, baseCur } from './store.js';
import { orderedDests, orderedTypes } from './model.js';

const OP_KEYS = new Set(['+', '−', '×', '÷']);

/**
 * اللوحة اليومية: أرقام فقط ثلاثة أعمدة — مفاتيح عريضة مريحة.
 * الفاصلة العشرية حلّت محلّ «٠٠»: إدخال 12.50 مستحيل بدونها، أما 100 فثلاث ضغطات.
 * المُعاملات ليست هنا عمداً — تخدم حالة نادرة، ووجودها يضيّق مفاتيح الأرقام
 * التي تُضغط في كل حركة. مكانها نافذة الحاسبة التي تُفتح بالضغط على المبلغ.
 */
export function keypad(){
  const keys = ['1','2','3','4','5','6','7','8','9','.','0','⌫'].map(k =>
    `<button class="key${k === '⌫' ? ' back' : ''}" data-key="${k}" type="button"
      aria-label="${k === '⌫' ? 'حذف' : k}">${k}</button>`
  ).join('');
  return `<div class="keys">${keys}</div>`;
}

/** لوحة الحاسبة الكاملة — داخل النافذة وحدها، حيث المساحة متاحة. */
export function calcKeypad(){
  const rows = [
    ['7', '8', '9', '÷'],
    ['4', '5', '6', '×'],
    ['1', '2', '3', '−'],
    ['.', '0', '⌫', '+'],
  ];
  const keys = rows.flat().map(k => {
    const cls = OP_KEYS.has(k) ? 'key op' : (k === '⌫' ? 'key back' : 'key');
    return `<button class="${cls}" data-calckey="${k}" type="button"
      aria-label="${k === '⌫' ? 'حذف' : k}">${k}</button>`;
  }).join('');
  return `<div class="keys calc">${keys}</div>`;
}

export function icon(id, size = 18){
  return `<svg width="${size}" height="${size}" aria-hidden="true"><use href="#${id}"/></svg>`;
}

export function amountBox(amount, currency, caption = '', income = false){
  const empty = amount === '' || amount == null;
  const sym = (CURRENCIES[currency] || CURRENCIES[baseCur()] || { sym: '' }).sym;
  return `<div class="amount">
    <div class="n mono${empty ? ' empty' : ''}${income ? ' income' : ''}">${money(+(amount || 0))}<em>${sym}</em></div>
    <div class="cap">${caption}</div>
  </div>`;
}

/**
 * صندوق المبلغ بحالة الحاسبة: سطر التعبير فوق النتيجة.
 * التعبير يظهر فقط حين توجد عملية فعلية — فلا يضيف ضجيجاً على الحالة الشائعة
 * (رقم واحد)، ويطمئن المستخدم إلى ما يُحسب حين يستخدم العمليات.
 */
/**
 * صندوق المبلغ. الرقم نفسه زرّ يفتح نافذة الحاسبة —
 * مسار متقدّم خلف إيماءة واحدة، بلا أن يكلّف الحالة الشائعة شيئاً.
 */
export function calcBox(state, currency, caption = '', income = false){
  const val = calc.value(state);
  const sym = (CURRENCIES[currency] || CURRENCIES[baseCur()] || { sym: '' }).sym;

  return `<div class="amount">
    <button type="button" class="n mono${val === null ? ' empty' : ''}${income ? ' income' : ''}"
            data-act="calc" aria-label="افتح الحاسبة">
      ${calc.formatValue(val === null ? 0 : val)}<em>${sym}</em>
      <span class="calchint" aria-hidden="true">${calcIcon()}</span>
    </button>
    <div class="cap">${caption}</div>
  </div>`;
}

function calcIcon(){
  return `<svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor"
    stroke-width="1.8" stroke-linecap="round"><rect x="4.5" y="2.5" width="15" height="19" rx="2.5"/>
    <path d="M8 7h8M8.5 12h.01M12 12h.01M15.5 12h.01M8.5 16.5h.01M12 16.5h.01M15.5 16.5h.01"/></svg>`;
}

/* جزءا النافذة المتغيّران، مستقلَّين عن الغلاف ليُحدَّثا وحدهما. */
function calcDisplayHtml(state, currency){
  const val = calc.value(state);
  const sym = (CURRENCIES[currency] || CURRENCIES[baseCur()] || { sym: '' }).sym;
  const invalid = val === null && !calc.isEmpty(state);
  return `<div class="calcdisplay">
      <div class="expr mono">${esc(calc.expression(state)) || '&nbsp;'}</div>
      <div class="res mono${invalid ? ' invalid' : ''}">
        ${invalid ? 'لا يمكن القسمة على صفر'
                  : `${calc.formatValue(val === null ? 0 : val)} <small>${sym}</small>`}
      </div>
    </div>`;
}

function calcCtaHtml(state, currency){
  const val = calc.value(state);
  const sym = (CURRENCIES[currency] || CURRENCIES[baseCur()] || { sym: '' }).sym;
  const ok = val !== null && val > 0;
  return `<button type="button" class="cta" data-calcact="accept" ${ok ? '' : 'disabled'}>
      استخدم ${ok ? calc.formatValue(val) + ' ' + sym : 'النتيجة'}
    </button>`;
}

/** محتوى نافذة الحاسبة — يُبنى في app.js ويُحقن في الحاوية العامة. */
export function calcModal(state, currency){
  return `<div class="sheet" role="dialog" aria-label="حاسبة">
    <div class="sheethandle" aria-hidden="true"></div>
    <div class="calchead">
      <span class="t">حاسبة</span>
      <button type="button" class="btn sm" data-calcact="clear">مسح</button>
      <button type="button" class="iconbtn" data-calcact="cancel" aria-label="إغلاق">
        ${icon('i-x', 16)}
      </button>
    </div>

    ${calcDisplayHtml(state, currency)}

    ${calcKeypad()}

    ${calcCtaHtml(state, currency)}
  </div>`;
}

/**
 * يحدّث الجزأين المتغيّرين فقط دون المساس بالغلاف.
 * إعادة بناء النافذة كاملةً عند كل ضغطة كانت تُعيد تشغيل حركة الانزلاق،
 * فتهتزّ تحت الإصبع مع كل رقم — وتُفقد حالة الضغط على المفتاح.
 */
export function updateCalcModal(sheet, state, currency){
  sheet.querySelector('.calcdisplay').outerHTML = calcDisplayHtml(state, currency);
  sheet.querySelector('.cta').outerHTML = calcCtaHtml(state, currency);
}

/**
 * ورقة المساعدة: تفتح على قسم الشاشة الحالية مباشرة.
 * الأقسام الأخرى متاحة من شريط سفلي، فمن أراد الدليل كاملاً وجده،
 * ومن أراد شاشته وحدها لم يدفع ثمن البحث.
 */
export function helpSheet(t, key, order){
  const shots = (t.shots || []).map(sh => `
    <figure class="hshot">
      <img src="${sh.src}" alt="${esc(sh.cap)}" loading="lazy" decoding="async">
      <figcaption>${esc(sh.cap)}</figcaption>
    </figure>`).join('');

  const points = (t.points || []).map(p => `<li>${p}</li>`).join('');

  const more = (order || []).map(k => `
    <button type="button" class="pill${k === key ? ' on' : ''}" data-helpgo="${k}">
      ${esc(TITLES[k] || k)}</button>`).join('');

  return `<div class="sheet help" role="dialog" aria-label="دليل ${esc(t.title)}">
    <div class="sheethandle" aria-hidden="true"></div>
    <div class="calchead">
      <span class="t">${esc(t.title)}<em>${esc(t.sub || '')}</em></span>
      <button type="button" class="iconbtn" data-helpact="close" aria-label="إغلاق">
        ${icon('i-x', 16)}
      </button>
    </div>
    <div class="hbody">
      ${shots ? `<div class="hshots">${shots}</div>` : ''}
      <ul class="hpoints">${points}</ul>
    </div>
    <div class="hnav"><div class="strip">${more}</div></div>
  </div>`;
}

// عناوين مختصرة للتنقّل بين الأقسام — تُملأ من help.js عند الإقلاع
export const TITLES = {};
export function setHelpTitles(map){ Object.assign(TITLES, map); }

/** جملة «الوجهة × النوع» التي تتكوّن أمام المستخدم أثناء الاختيار. */
export function sentence(destId, typeId){
  const d = destId ? `<span class="kd">${esc(destName(destId))}</span>` : '<span class="ph">وجهة؟</span>';
  const t = typeId ? `<span class="kt">${esc(typeName(typeId))}</span>` : '<span class="ph">نوع اختياري</span>';
  return `${d} <span class="ph">×</span> ${t}`;
}

// البنود تُسحَب أفقياً بالإصبع — الحركة الطبيعية على الموبايل.
// وزر «＋ وجهة» في آخر الشريط لا أوّله، فلا يزاحم البنود الأكثر استخداماً
// التي رُتّبت لتكون أول ما تلمسه اليد.
export function destStrip(selectedId, { auto = false, addBtn = true, wrap = false,
  label = 'الوجهة — لمن أو لأين' } = {}){
  const pills = orderedDests().map(d => {
    const on = d.id === selectedId;
    return `<button type="button" class="pill${auto && on ? ' is-auto' : ''}"
      data-dest="${d.id}" aria-pressed="${on}">${esc(d.name)}</button>`;
  }).join('');
  const add = addBtn ? '<button type="button" class="pill add" data-add="dest">＋ وجهة</button>' : '';
  return `<div class="dim d"><div class="lab">${label}</div>
    <div class="strip${wrap ? ' wrap' : ''}">${pills}${add}</div></div>`;
}

export function typeStrip(selectedId, { addBtn = true, wrap = false, order = null,
  label = 'النوع — على ماذا', hint = 'اختياري' } = {}){
  const list = order || orderedTypes();
  const pills = list.map((t, i) => `<button type="button" class="pill${order && i === 0 ? ' is-auto' : ''}"
      data-type="${t.id}" aria-pressed="${t.id === selectedId}">${esc(t.name)}</button>`).join('');
  const add = addBtn ? '<button type="button" class="pill add" data-add="type">＋ نوع</button>' : '';
  return `<div class="dim t"><div class="lab">${label}${hint ? `<span class="hintx">${hint}</span>` : ''}</div>
    <div class="strip${wrap ? ' wrap' : ''}">${pills}${add}</div></div>`;
}

/** رقاقات العملة — تُستخدم في شاشة التسجيل وحدها، لا عند كل حركة. */
export function currencyChips(current, attr = 'cur'){
  return Object.keys(CURRENCIES).slice(0, 3).map(c =>
    `<button type="button" class="chip" data-${attr}="${c}" aria-pressed="${c === current}">
       ${CURRENCIES[c].name} ${CURRENCIES[c].sym}</button>`
  ).join('');
}

/** صف حركة في السجل أو التقرير. */
export function entryRow(e, { showDate = false } = {}){
  const t = e.typeId ? `<span class="tag t">${esc(typeName(e.typeId) || '')}</span>`
                     : '<span class="tag non">بلا نوع</span>';
  const dateTag = showDate ? `<span class="tag plain">${esc(shortDate(new Date(e.occurredAt)))}</span>` : '';
  const sym = (CURRENCIES[e.currency] || { sym: '' }).sym;
  // الملاحظة سطرٌ تحت الحركة — ولا تظهر إن لم تُكتب، فلا تزحم السجلَّ بفراغ
  const note = e.note
    ? `<span class="rownote">${icon('i-note', 13)}<span>${esc(e.note)}</span></span>`
    : '';

  return `<button type="button" class="row${e.kind === 'in' ? ' income' : ''}" data-entry="${e.id}">
    <span class="tags">
      <span class="tag ${e.kind === 'in' ? 'i' : 'd'}">${esc(destName(e.destId))}</span>
      ${e.kind === 'in' ? '<span class="tag i">دخل</span>' : t}
      ${dateTag}
    </span>
    <span class="val">${e.kind === 'in' ? '+' : ''}${money(e.amount)}<small>${sym}</small></span>
    ${note}
  </button>`;
}

/**
 * حالتا الشاشات التي تقرأ التاريخ.
 * التاريخ لم يعد مخزّناً على الجهاز — يُجلب لفترته عند العرض — فهذه الشاشات
 * تحتاج اتصالاً. نقولها صراحةً بدل عرض شاشة فارغة تبدو كأن البيانات ضاعت.
 */
export function loadingBlock(what = 'الحركات'){
  return `<div class="scroll"><div class="loadbox">
      <div class="spin" aria-hidden="true"></div>
      <span>يجري جلب ${esc(what)}…</span>
    </div></div>`;
}

export function offlineBlock(what = 'هذه الشاشة', extra = ''){
  return `<div class="scroll">${emptyState('يحتاج اتصالاً بالإنترنت',
      `${esc(what)} محفوظة على الخادم لا على الجهاز — وهذا ما يجعل التطبيق
       خفيفاً مهما طال تاريخ محفظتك.<br>سجّل حركاتك الآن كالمعتاد؛ تظهر هنا
       فور عودة الاتصال.${extra}`)}</div>`;
}

export function errorBlock(message){
  return `<div class="scroll">${emptyState('تعذّر الجلب', esc(message || 'حاول مرة أخرى.'))}</div>`;
}

/**
 * زرّ الدليل ملتصق باسم الشاشة لا في طرف الشريط.
 * سببان: الطرف تشغله عناصر أخرى (مبدّل مصروف/دخل) فيضيق العنوان ٣٥ بكسل،
 * والالتصاق بالاسم يقول معنى الزرّ بلا كلمة — تسأل عن *هذه* الشاشة.
 * الرمز صغير لكن منطقة اللمس موسّعة إلى ٤٤ بكسل عبر ::before في CSS.
 */
export function helpBtn(){
  return `<button type="button" class="helpdot" data-act="help"
    aria-label="دليل هذه الشاشة">${icon('i-help', 12)}</button>`;
}

/** كتلة العنوان مع زرّ الدليل — مشتركة بين الشريطين وشاشة الدخول. */
function titleBlock(title, sub){
  return `<div class="grow">
    <div class="ttl"><h1>${esc(title)}</h1>${helpBtn()}</div>
    ${sub ? `<div class="sub">${sub}</div>` : ''}
  </div>`;
}

export function topBar(title, sub, right = ''){
  return `<div class="bar">${titleBlock(title, sub)}${right}</div>`;
}

export function backBar(title, sub){
  return `<div class="bar">
    <button type="button" class="iconbtn" data-act="back" aria-label="رجوع">${icon('i-back', 17)}</button>
    ${titleBlock(title, sub)}
  </div>`;
}

export function emptyState(title, body){
  return `<div class="empty-state"><b>${esc(title)}</b>${body}</div>`;
}

export function bar(name, value, max, total, color, dataAttr = ''){
  const pct = total ? Math.round(value / total * 100) : 0;
  const w = max ? (value / max * 100) : 0;
  return `<button type="button" class="bar" ${dataAttr}>
    <div class="bh">
      <span class="nm">${esc(name)}</span>
      <span><span class="v">${fmt(value)}</span><span class="p">${pct}٪</span></span>
    </div>
    <div class="track"><div class="fill" style="width:${w}%;background:${color}"></div></div>
  </button>`;
}

export function deltaTag(now, was){
  if (was <= 0) return now > 0 ? '<span class="delta up">جديد</span>' : '';
  const p = Math.round((now - was) / was * 100);
  if (p === 0) return '<span class="delta flat">بلا تغيير</span>';
  return `<span class="delta ${p > 0 ? 'up' : 'down'}">${p > 0 ? '▲' : '▼'} ${Math.abs(p)}٪</span>`;
}

/** حوار إدخال نصي بسيط — يكفي لنسخة أولى؛ يستحق حواراً داخل التطبيق لاحقاً. */
export function askName(message, initial = ''){
  const v = window.prompt(message, initial);
  return v === null ? null : v.trim();
}
